import { useMemo, useState } from 'react';
import { FiCheckCircle, FiCopy, FiEdit3, FiLink, FiPlus, FiRefreshCw, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import type { NamedItem, Rompimento, RompimentoDraft } from '../../types';
import { Button, Spinner } from '../ui';
import { cn } from '../../lib/cn';
import { fmtDateTime } from '../../lib/occurrences';
import { areaLabel, copyToClipboard, rompMensagem, rompStatus } from '../../lib/rompimentos';
import RompimentoModal from './RompimentoModal';

interface RompimentosPageProps {
  rompimentos: Rompimento[];
  cities: NamedItem[];
  loading: boolean;
  onCreate: (draft: RompimentoDraft) => Promise<void>;
  onUpdate: (id: string, draft: RompimentoDraft) => Promise<void>;
  onFinalize: (id: string) => Promise<void>;
  onReopen: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onReload: () => void;
}

const STATUS_BADGE: Record<string, string> = {
  ativo: 'bg-red-100 text-red-700',
  agendado: 'bg-amber-100 text-amber-700',
  encerrado: 'bg-slate-100 text-slate-500'
};
const ORDER: Record<string, number> = { ativo: 0, agendado: 1, encerrado: 2 };

export default function RompimentosPage({
  rompimentos,
  cities,
  loading,
  onCreate,
  onUpdate,
  onFinalize,
  onReopen,
  onDelete,
  onReload
}: RompimentosPageProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Rompimento | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Página pública servida pelo painel (rota fora do login) — Fase 34.
  const publicUrl = `${window.location.origin}/rede/rompimentos`;

  const rows = useMemo(() => {
    return [...rompimentos].sort((a, b) => {
      const sa = ORDER[rompStatus(a).cls];
      const sb = ORDER[rompStatus(b).cls];
      if (sa !== sb) return sa - sb;
      return new Date(b.inicio || 0).getTime() - new Date(a.inicio || 0).getTime();
    });
  }, [rompimentos]);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (r: Rompimento) => {
    setEditing(r);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };
  const handleSave = async (draft: RompimentoDraft) => {
    if (editing) await onUpdate(editing._id, draft);
    else await onCreate(draft);
    closeModal();
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(publicUrl);
    if (ok) {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      window.prompt('Copie o link público dos rompimentos:', publicUrl);
    }
  };

  const copyAviso = async (r: Rompimento) => {
    const ok = await copyToClipboard(rompMensagem(r));
    window.alert(ok ? 'Aviso copiado! Cole no WhatsApp.' : 'Não foi possível copiar o aviso.');
  };

  const finalize = (r: Rompimento) => {
    if (!window.confirm(`Finalizar agora o rompimento "${r.titulo}"?\n\nO horário atual será registrado como término.`)) return;
    onFinalize(r._id).catch((e) => window.alert((e as Error).message || 'Erro ao finalizar.'));
  };
  const reopen = (r: Rompimento) => {
    if (!window.confirm('Reabrir este rompimento? O horário de fim será removido e ele voltará a "em andamento".')) return;
    onReopen(r._id).catch((e) => window.alert((e as Error).message || 'Erro ao reabrir.'));
  };
  const remove = (r: Rompimento) => {
    if (!window.confirm(`Excluir o rompimento "${r.titulo}"?\n\nEsta ação não pode ser desfeita.`)) return;
    onDelete(r._id).catch((e) => window.alert((e as Error).message || 'Erro ao excluir.'));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-bold text-slate-800">📡 Rompimentos</h2>
          <p className="text-sm text-slate-500">Alertas massivos de indisponibilidade com área no mapa e link público.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onReload} title="Recarregar">
            <FiRefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button variant={linkCopied ? 'success' : 'ghost'} size="sm" onClick={copyLink} title={publicUrl}>
            <FiLink size={15} /> {linkCopied ? 'Link copiado!' : 'Copiar link público'}
          </Button>
          <Button size="sm" onClick={openNew}>
            <FiPlus size={16} /> Novo Rompimento
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-slate-400">
            {loading ? (
              <>
                <Spinner className="h-6 w-6" /> Carregando rompimentos...
              </>
            ) : (
              <>
                <span className="text-4xl">📡</span>
                Nenhum rompimento registrado
                <span className="text-xs">Clique em “Novo Rompimento” para registrar um evento.</span>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map((r) => {
              const st = rompStatus(r);
              const previsaoVencida = !r.fim && r.previsao && new Date(r.previsao).getTime() < Date.now();
              return (
                <div key={r._id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-bold text-slate-800">{r.titulo || '(sem título)'}</span>
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-bold', STATUS_BADGE[st.cls])}>{st.label}</span>
                  </div>

                  <div className="mt-2 space-y-1 text-sm text-slate-600">
                    <div>
                      🕒 {fmtDateTime(r.inicio)} →{' '}
                      {r.fim ? fmtDateTime(r.fim) : <span className="font-semibold text-emerald-600">em andamento</span>}
                    </div>
                    <div>⭕ {areaLabel(r)}</div>
                    {r.previsao && (
                      <div>
                        🔧 Previsão: {fmtDateTime(r.previsao)}
                        {previsaoVencida && <span className="font-semibold text-amber-600"> (vencida — atualize)</span>}
                      </div>
                    )}
                    {r.descricao && <div className="italic text-slate-500">"{r.descricao}"</div>}
                  </div>

                  {(r.cidades?.length > 0 || r.bairros?.length > 0) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.cidades?.map((c) => (
                        <span key={'c' + c} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          🏙 {c}
                        </span>
                      ))}
                      {r.bairros?.map((b) => (
                        <span key={'b' + b} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                          📍 {b}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-3">
                    {r.fim ? (
                      <Button variant="ghost" size="sm" onClick={() => reopen(r)}>
                        <FiRotateCcw size={14} /> Reabrir
                      </Button>
                    ) : (
                      <Button variant="success" size="sm" onClick={() => finalize(r)}>
                        <FiCheckCircle size={14} /> Finalizar
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => copyAviso(r)} title="Copiar aviso de WhatsApp">
                      <FiCopy size={14} /> Aviso
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>
                      <FiEdit3 size={14} /> Editar
                    </Button>
                    <button
                      onClick={() => remove(r)}
                      title="Excluir"
                      className="ml-auto rounded-md p-2 text-red-600 hover:bg-red-50"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modalOpen && <RompimentoModal rompimento={editing} cities={cities} onClose={closeModal} onSave={handleSave} />}
    </div>
  );
}
